import { candidateScoreOf } from '../../utils/convertQuestionnaire';

type RequestParameter = Http.Connection.Request.Parameter.EverydayNote.Create
    | Http.Connection.Request.Parameter.EverydayNote.Update;

/**
 * フォームの値をバックエンドに送るフォーマットに変換する
 */
export const convertSubmittableValue = (
    values: EverydayNote.Form.Control.Questionnaire,
    isCopy: boolean
): RequestParameter => {
    const {
        afterMessage,
        allowPastDateAnswer,
        canComment,
        canAdminSetPublic,
        duration,
        isDesignatedTerm,
        isHolidayAnswerDisallowed,
        isOnceADay,
        isOverwritePublish,
        isRequired,
        isSetRecordableTime,
        isViewedHome,
        items,
        publishFrom,
        publishToAll,
        recordableDayOfWeeks,
        recordableTimeEnd,
        recordableTimeStart,
        targetGrades,
        targetOrganizations,
        targetPermissions,
        targetTerm,
        uuid,
        name,
        description,
        explanation,
        inlineFiles,
        // 現状選択しないので固定値
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        type: _type,
    } = values;

    const base = {
        after_message: afterMessage,
        allow_past_date_answer: allowPastDateAnswer,
        can_admin_set_public: canAdminSetPublic,
        can_comment: canComment,
        day_of_weeks: isSetRecordableTime ?
            recordableDayOfWeeks :
            [0, 1, 2, 3, 4, 5, 6],
        description: description,
        end_time: isSetRecordableTime ?
            recordableTimeEnd?.format('HH:mm') ?? '23:59' :
            '23:59',
        is_designated_term: isDesignatedTerm,
        // 「回答可能な時間の指定」をしない場合は曜日と同様に祝日制限も外す
        is_holiday_answer_disallowed: isSetRecordableTime ? isHolidayAnswerDisallowed : false,
        is_once_a_day: isOnceADay,
        is_required: isRequired,
        is_viewed_home: isViewedHome,
        items: items.map(convertItem(isCopy)),
        name,
        // 保存時には必ず 公開終了時刻 が設定されるため、undefined とはならないが、
        // 適切な型付けが大変なので、一旦強制的に対応する
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        publish_end_at: duration[1]!.unix(),
        publish_from: publishFrom,
        publish_start_at: duration[0].unix(),
        publish_to_all: publishToAll,
        start_time: isSetRecordableTime ?
            recordableTimeStart?.format('HH:mm') ?? '00:00' :
            '00:00',
        target_grades: targetGrades,
        target_organizations: targetOrganizations.map(e => e.uuid),
        target_permissions: targetPermissions,
        target_term: targetTerm ?? null,
        type: 1 as EverydayNote.QuestionnaireTypes,
        explanation: explanation,
        // true のときのみ送る
        ...(isOverwritePublish ? { is_overwrite_publish: isOverwritePublish[0] } : {}),
        files: inlineFiles.map((uuid: string) => ({ file_uuid: uuid, is_inline: true }))
    };

    return isCopy || uuid === undefined ?
        base :
        { ...base, uuid };
};

const convertItem = (isCopy: boolean) => (
    item: Parameters<typeof convertSubmittableValue>[0]['items'][number],
    index: number,
): RequestParameter['items'][number] => {
    const {
        candidates,
        evaluationScaleMax,
        isRequired,
        uuid,
        order,
        sliderRangeMax,
        sliderRangeMin,
        sliderInitialUserValue,
        sliderRangeMinDetail,
        sliderRangeMaxDetail,
        sliderInitialUserValueDetail,
        showSliderValueDetails,
        markDetails,
        candidateOrderPositive,
        ...restItem
    } = item;

    // 段階選択式（type=10）は序列設定と並び順から各選択肢のスコアを算出して送る
    const candidatesPayload = candidates.map((candidate, i) => ({
        content: candidate.content,
        order: candidate.order,
        uuid: isCopy ? undefined : candidate.uuid,
        ...(restItem.type === 10
            ? { score: candidateScoreOf(i, candidates.length, candidateOrderPositive ?? true) }
            : {}),
    }));

    return isCopy || uuid === undefined ?
        {
            candidates: candidatesPayload,
            is_required: isRequired,
            order: order ?? index,
            ...(restItem.type === 6 ? { 
                evaluation_scale_max: evaluationScaleMax,
                mark_details: markDetails ? markDetails : Array(evaluationScaleMax).fill(''),
            } : {}),
            ...(restItem.type === 8 ? { 
                slider_range_max: sliderRangeMax,
                slider_range_min: sliderRangeMin,
                slider_initial_user_value: sliderInitialUserValue,
                slider_range_max_detail: sliderRangeMaxDetail,
                slider_range_min_detail: sliderRangeMinDetail,
                slider_initial_user_value_detail: sliderInitialUserValueDetail,
                show_slider_value_details: showSliderValueDetails,
            } : {}),
            ...((restItem.type === 4) ? { 
                    mark_details: markDetails ? markDetails : Array(5).fill(''),
            } : {}),
            ...((restItem.type === 9) ? { 
                    mark_details: markDetails ? markDetails : Array(10).fill(''),
            } : {}),
            ...restItem,
        } :
        {
            candidates: candidatesPayload,
            is_required: isRequired,
            order: order ?? index,
            uuid,
            ...(restItem.type === 6 ? { 
                evaluation_scale_max: evaluationScaleMax,
                mark_details: markDetails ? markDetails : Array(evaluationScaleMax).fill(''),
            } : {}),
            ...(restItem.type === 8 ? { 
                slider_range_max: sliderRangeMax,
                slider_range_min: sliderRangeMin,
                slider_initial_user_value: sliderInitialUserValue,
                slider_range_max_detail: sliderRangeMaxDetail,
                slider_range_min_detail: sliderRangeMinDetail,
                slider_initial_user_value_detail: sliderInitialUserValueDetail,
                show_slider_value_details: showSliderValueDetails,
            } : {}),
            ...((restItem.type === 4) ? { 
                    mark_details: markDetails ? markDetails : Array(5).fill(''),
            } : {}),
            ...((restItem.type === 9) ? { 
                    mark_details: markDetails ? markDetails : Array(10).fill(''),
            } : {}),
            ...restItem,
        };
};
